import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NotificationTypes } from '@chegg-ui/notification-banner';
import { defaultSocialsProviders } from "../../common/conts";
import { useIsOpenPass } from "../../common/hooks/openPass.hook";
import { getTexts } from "../../lib/contentful/defaultTexts";
import { interactionsRioEvent, notificationsRioEvent } from "../../lib/rio/rioEvents";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { emailFocus, flipView, passwordBlur, passwordFocus, removeSsoCredentials, setEmail, setEmailError, setPassword } from "../../store/reducer";
import { sdkLogin } from "../../store/reducers/sdkLogin";
import { emailBlur, setGenericError, setLoading } from "../../store/sharedActions";
import { PxCaptchaTypes, View } from "../../store/types";
import { isValidEmail } from "../../utils/passwordValidations";
import Header from "../Header";
import Message from "../Message";
import PasswordInput from "../PasswordInput";
import PxCaptcha from "../PxCaptcha";
import Social from "../Social";
import { useSendStartEvents } from "../hooks/useSendStartEvents";
import { EmailInput, FlipViewButton, Footer, SocialContainer, SubmitButton } from "../shared.styles";
import { CredentialsView, Form, LoginView } from "./Login.styles";
var Login = function Login(props) {
  var options = props.options,
    styles = props.styles;
  var isSso = useAppSelector(function (state) {
    return state.signup.isSso;
  });
  var pxCaptchaType = useAppSelector(function (state) {
    return state.pxCaptchaType;
  });
  var showBack = useAppSelector(function (state) {
    return state.showBack;
  });
  var backView = useAppSelector(function (state) {
    return state.backView;
  });
  var frontView = useAppSelector(function (state) {
    return state.frontView;
  });
  var view = showBack && backView || frontView;
  var credentials = useAppSelector(function (state) {
    return state.signin.credentials;
  });
  var loading = useAppSelector(function (state) {
    return state.signin.loading;
  });
  var ssoError = useAppSelector(function (state) {
    return state.sso.error;
  });
  var dispatch = useAppDispatch();
  var email = credentials.email,
    password = credentials.password,
    error = credentials.error;
  var value = email.value,
    valid = email.valid,
    focus = email.focus,
    changed = email.changed,
    touched = email.touched,
    emailError = email.error;
  var passwordInputEl = useRef(null);
  var emailInputEl = useRef(null);
  var _useState = useState(email.valid && password.value.length > 0),
    _useState2 = _slicedToArray(_useState, 2),
    isValid = _useState2[0],
    setValid = _useState2[1];
  var _useState3 = useState((options === null || options === void 0 ? void 0 : options.ssoProviders) || defaultSocialsProviders),
    _useState4 = _slicedToArray(_useState3, 2),
    providers = _useState4[0],
    setProviders = _useState4[1];
  var _useIsOpenPass = useIsOpenPass(),
    isEnabled = _useIsOpenPass.isEnabled,
    clientReady = _useIsOpenPass.clientReady;
  var _getTexts = getTexts(),
    _getTexts$login = _getTexts.login,
    title = _getTexts$login.title,
    subTitle = _getTexts$login.subTitle,
    loginSubmitButtonTitle = _getTexts$login.loginSubmitButtonTitle,
    invalidEmailError = _getTexts$login.invalidEmailError,
    emptyEmailError = _getTexts$login.emptyEmailError,
    emailInputLabel = _getTexts$login.emailInputLabel,
    emailPlaceHolder = _getTexts$login.emailPlaceHolder,
    passwordInputLabel = _getTexts$login.passwordInputLabel,
    passwordInputPlaceHolder = _getTexts$login.passwordInputPlaceHolder,
    dontHaveAccount = _getTexts$login.dontHaveAccount,
    footerSignupText = _getTexts$login.footerSignupText,
    footerForgotPasswordText = _getTexts$login.footerForgotPasswordText;
  useSendStartEvents(View.SIGNIN);
  useEffect(function () {
    setTimeout(function () {
      try {
        if (passwordInputEl.current.matches(':-internal-autofill-selected ') && !password.value) {
          setValid(true);
        }
      } catch (e) {
        //
      }
    }, 1000);
    if (emailInputEl.current) {
      emailInputEl.current.focus();
    }
  }, []);
  useEffect(function () {
    setValid(email.valid && password.value.length > 0);
  }, [email.valid, password.value]);
  useEffect(function () {
    if (!valid && !focus && touched && changed && !emailError && value.length) {
      dispatch(setEmailError({
        error: invalidEmailError
      }));
      notificationsRioEvent({
        notifications: {
          type: 'error',
          message_name: 'sign in error',
          message_detail: invalidEmailError,
          message_id: 'sign in: email invalid error'
        }
      });
    }
  }, [value, focus, valid, touched, changed, emailError, dispatch, invalidEmailError]);

  // rio event that is sent when user starts typing email
  useEffect(function () {
    if (email.touched && email.focus && email.changed && email.valueChanged) {
      interactionsRioEvent({
        interaction: {
          type: 'start',
          element: {
            type: 'text',
            name: 'email entry form',
            region_name: 'sign in auth page'
          }
        }
      });
    }
  }, [email.touched, email.changed, email.focus, email.valueChanged]);

  // rio event that is sent when user starts typing password
  useEffect(function () {
    if (password.touched && password.focus && password.changed && password.valueChanged) {
      interactionsRioEvent({
        interaction: {
          type: 'start',
          element: {
            type: 'text',
            name: 'password entry form',
            region_name: 'sign in auth page'
          }
        }
      });
    }
  }, [password.touched, password.changed, password.focus, password.valueChanged]);
  useEffect(function () {
    /* istanbul ignore next */
    if (isEnabled && clientReady) {
      setProviders(function (prev) {
        return [].concat(_toConsumableArray(prev.filter(function (elem) {
          return elem !== 'openPass';
        })), ['openPass']);
      });
    } else {
      setProviders((options === null || options === void 0 ? void 0 : options.ssoProviders) || defaultSocialsProviders);
    }
  }, [isEnabled, clientReady, options]);
  var onPasswordChange = useCallback(function (password) {
    dispatch(setPassword({
      password: password
    }));
  }, [dispatch]);
  var onPasswordBlur = useCallback(function () {
    dispatch(passwordBlur());
  }, [dispatch]);
  var onPasswordFocus = useCallback(function () {
    dispatch(passwordFocus());
  }, [dispatch]);
  var onEmailBlur = useCallback(function () {
    dispatch(emailBlur());
    if (!value.length && changed) {
      dispatch(setEmailError({
        error: emptyEmailError
      }));
      notificationsRioEvent({
        notifications: {
          type: 'error',
          message_name: 'sign in error',
          message_detail: emptyEmailError,
          message_id: 'sign in: email empty error'
        }
      });
      return;
    } else if (!isValidEmail(value) && changed) {
      dispatch(setEmailError({
        error: invalidEmailError
      }));
      notificationsRioEvent({
        notifications: {
          type: 'error',
          message_name: 'sign in error',
          message_detail: invalidEmailError,
          message_id: 'sign in: email invalid error'
        }
      });
      return;
    }
  }, [changed, dispatch, emptyEmailError, invalidEmailError, value]);
  var onEmailFocus = function onEmailFocus() {
    dispatch(emailFocus());
  };

  /* istanbul ignore next */
  var headerStyle = styles === null || styles === void 0 ? void 0 : styles.header;
  return /*#__PURE__*/React.createElement(LoginView, {
    "data-test": "login_view"
  }, /*#__PURE__*/React.createElement(Header, {
    "data-test": "login_",
    styles: headerStyle,
    title: (options === null || options === void 0 ? void 0 : options.title) || title,
    subTitle: (options === null || options === void 0 ? void 0 : options.subtitle) || subTitle
  }), /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(CredentialsView, null, /*#__PURE__*/React.createElement(EmailInput, {
    ref: emailInputEl,
    inputId: "login_email_input",
    "data-test": "login_email_input",
    onFocus: onEmailFocus,
    value: value,
    labelText: emailInputLabel,
    onChange: onEmailChange,
    onBlur: onEmailBlur,
    placeholder: emailPlaceHolder,
    autoComplete: "email",
    errorText: emailError || undefined
  }), /*#__PURE__*/React.createElement(PasswordInput, {
    ref: passwordInputEl,
    inputId: "login_password_input",
    "data-test": "login_password_input",
    value: password.value,
    name: "login_password_input",
    placeholder: passwordInputPlaceHolder,
    labelText: passwordInputLabel,
    onChange: onPasswordChange,
    autoComplete: "current-password",
    onBlur: onPasswordBlur,
    onFocus: onPasswordFocus
  })), renderMessage()), renderPxCaptcha(), renderSocialButtons(), /*#__PURE__*/React.createElement(SubmitButton, {
    type: "submit",
    size: "medium",
    "data-test": "login_submit_button",
    isLoading: loading,
    disabled: !isValid
  }, (options === null || options === void 0 ? void 0 : options.buttonTitle) || loginSubmitButtonTitle)), renderFooter());
  function renderMessage() {
    if (error && !ssoError) {
      return /*#__PURE__*/React.createElement(Message, {
        type: NotificationTypes.Error,
        text: error
      });
    }
  }
  function renderFooter() {
    return /*#__PURE__*/React.createElement(Footer, null, /*#__PURE__*/React.createElement("p", {
      "data-test": "dont_have_account"
    }, dontHaveAccount, ' ', /*#__PURE__*/React.createElement(FlipViewButton, {
      "data-test": "flip_to_signup",
      onClick: onSignUpFlipViewButtonClick
    }, footerSignupText)), /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement(FlipViewButton, {
      "data-test": "flip_to_reset_password",
      onClick: onResetPasswordFlipViewButtonClick
    }, footerForgotPasswordText)));
  }
  function onSubmit(e) {
    e.preventDefault();
    if (!loading) {
      /* istanbul ignore next */
      if (isSso) {
        dispatch(removeSsoCredentials());
      }
      dispatch(sdkLogin());
    }
  }
  function onEmailChange(email) {
    dispatch(setEmail({
      email: email
    }));
  }
  function renderSocialButtons() {
    return /*#__PURE__*/React.createElement(SocialContainer, null, /*#__PURE__*/React.createElement(Social, {
      providers: providers
    }), renderSocialMessage());
  }
  function renderSocialMessage() {
    if (ssoError && !error) {
      return /*#__PURE__*/React.createElement(Message, {
        type: NotificationTypes.Error,
        text: ssoError,
        noMargin: true
      });
    }
  }
  function onSignUpFlipViewButtonClick() {
    interactionsRioEvent({
      interaction: {
        type: 'click',
        element: {
          type: 'link',
          name: 'flip to sign up page',
          region_name: 'sign in auth page'
        }
      }
    });
    dispatch(flipView({
      view: View.SIGNUP
    }));
    if (pxCaptchaType === PxCaptchaTypes.LOGIN) {
      dispatch(setGenericError({
        error: '',
        onView: View.SIGNIN
      }));
      dispatch(setLoading({
        loading: false,
        onView: View.SIGNIN
      }));
    }
  }
  function onResetPasswordFlipViewButtonClick() {
    interactionsRioEvent({
      interaction: {
        type: 'click',
        element: {
          type: 'link',
          name: 'flip to reset password',
          region_name: 'sign in auth page'
        }
      }
    });
    dispatch(flipView({
      view: View.RESET_PASSWORD
    }));
    if (pxCaptchaType === PxCaptchaTypes.LOGIN) {
      dispatch(setGenericError({
        error: '',
        onView: View.SIGNIN
      }));
      dispatch(setLoading({
        loading: false,
        onView: View.SIGNIN
      }));
    }
  }
  function renderPxCaptcha() {
    if (pxCaptchaType !== PxCaptchaTypes.LOGIN || view !== View.SIGNIN) {
      return null;
    }
    return /*#__PURE__*/React.createElement(PxCaptcha, {
      dataTest: "px_captcha_login"
    });
  }
};
export default Login;